import type { LocalDate } from "@js-joda/core";
import type { CompanyGigShift } from "model/Gig";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";

import type {
  SelectedWorker,
  WorkerEmploymentPeriod,
} from "../modal/gigFormModal.types";

export type WorkersWithoutEmploymentByShiftIndex = Readonly<
  Record<number, readonly SelectedWorker[]>
>;

type ShiftDateRange = {
  readonly endDate: LocalDate;
  readonly startDate: LocalDate;
};

type EmploymentDateRange = {
  readonly endDate: LocalDate | null;
  readonly startDate: LocalDate;
};

type EmploymentWarningsInput = {
  readonly selectedWorkers: readonly SelectedWorker[];
  readonly shifts: readonly CompanyGigShift[];
};

type EmploymentRangeInput = {
  readonly shiftRange: ShiftDateRange;
  readonly worker: SelectedWorker;
};

type ShiftWorkersInput = {
  readonly selectedWorkers: readonly SelectedWorker[];
  readonly shift: CompanyGigShift;
};

function getShiftDateRange(shift: CompanyGigShift): ShiftDateRange | null {
  const startDate = parseIsoDateToLocalDateOrNull(shift.startTime);
  const endDate = parseIsoDateToLocalDateOrNull(shift.endTime);
  if (startDate == null || endDate == null || endDate.isBefore(startDate)) {
    return null;
  }

  return { endDate, startDate };
}

function getEmploymentDateRange(
  period: WorkerEmploymentPeriod,
): EmploymentDateRange | null {
  const startDate = parseIsoDateToLocalDateOrNull(period.startDate);
  if (startDate == null) return null;

  if (period.endDate == null) {
    return { endDate: null, startDate };
  }

  const endDate = parseIsoDateToLocalDateOrNull(period.endDate);
  if (endDate == null || endDate.isBefore(startDate)) return null;

  return { endDate, startDate };
}

function getEmploymentRanges(worker: SelectedWorker): EmploymentDateRange[] {
  return [worker.employmentInfo.current, ...worker.employmentInfo.employments]
    .map(getEmploymentDateRange)
    .filter((range): range is EmploymentDateRange => range != null)
    .sort((left, right) => left.startDate.compareTo(right.startDate));
}

function isEmployedThroughoutRange({
  shiftRange,
  worker,
}: EmploymentRangeInput): boolean {
  let nextDate = shiftRange.startDate;

  for (const employmentRange of getEmploymentRanges(worker)) {
    if (
      employmentRange.endDate != null &&
      employmentRange.endDate.isBefore(nextDate)
    ) {
      continue;
    }
    if (employmentRange.startDate.isAfter(nextDate)) return false;
    if (
      employmentRange.endDate == null ||
      !employmentRange.endDate.isBefore(shiftRange.endDate)
    ) {
      return true;
    }
    nextDate = employmentRange.endDate.plusDays(1);
  }

  return false;
}

function getWorkersWithoutEmployment({
  selectedWorkers,
  shift,
}: ShiftWorkersInput): readonly SelectedWorker[] {
  const shiftRange = getShiftDateRange(shift);
  if (shiftRange == null) return selectedWorkers;

  return selectedWorkers.filter(
    (worker) => !isEmployedThroughoutRange({ shiftRange, worker }),
  );
}

export function getWorkersWithoutEmploymentByShiftIndex(
  input: EmploymentWarningsInput,
): WorkersWithoutEmploymentByShiftIndex {
  const workersByShiftIndex: Record<number, readonly SelectedWorker[]> = {};

  input.shifts.forEach((shift, index) => {
    const workersWithoutEmployment = getWorkersWithoutEmployment({
      selectedWorkers: input.selectedWorkers,
      shift,
    });

    if (workersWithoutEmployment.length > 0) {
      workersByShiftIndex[index] = workersWithoutEmployment;
    }
  });

  return workersByShiftIndex;
}
